import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';
import { 
  FiMaximize, FiMinimize, FiZoomIn, FiZoomOut,
  FiChevronDown, FiChevronRight, FiX,
  FiGitBranch, FiCrosshair, FiMaximize2,
  FiTarget, FiEye, FiEyeOff,
  FiBox, FiLayers, FiGrid, FiList
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';
import { alluvialMapping, getExactApproach, getApproachLayers, getLayerName, getAllRequirementCategories } from '../../../data/alluvialMapping';
import { getGroupedComponents, COMPONENT_GROUPINGS } from '../../../data/componentGroupings';

// ==================== CONSTANTS ====================

const APPROACH_NAMES = alluvialMapping.approachNames;
const APPROACH_ICONS = alluvialMapping.approachIcons;

const REQUIREMENT_ICONS = {
  'business': '💼', 'functional': '⚙️', 'technical': '🔧',
  'data': '💾', 'security': '🛡️', 'performance': '⚡',
  'compliance': '⚖️', 'operational': '🔧'
};

const REQUIREMENT_COLORS = {
  'business': '#3b82f6', 'functional': '#10b981', 'technical': '#f59e0b',
  'data': '#8b5cf6', 'security': '#ef4444', 'performance': '#06b6d4',
  'compliance': '#ec4899', 'operational': '#f97316'
};

const VIEW_MODES = {
  HIERARCHICAL: 'hierarchical',
  DIRECT: 'direct'
};

const GROUPING_MODES = {
  NONE: 'none',
  TYPE: 'type',
  SEQUENCE: 'sequence'
};

// ==================== HELPER ====================

const getRequirementStatus = (reqId, components) => {
  const statusMap = {
    'business-value': components.length > 0 ? 'satisfied' : 'partial',
    'data-processing': components.some(c => c.layerId === 'layer5' || c.layerId === 'layer3') ? 'satisfied' : 'partial',
    'model-inference': components.some(c => c.layerId === 'layer6' || c.layerId === 'layer8') ? 'satisfied' : 'partial',
    'scalable-infrastructure': components.some(c => c.layerId === 'layer1' || c.layerId === 'layer9') ? 'satisfied' : 'partial',
    'data-storage': components.some(c => c.layerId === 'layer4') ? 'satisfied' : 'partial',
    'data-protection': components.some(c => c.tags?.includes('security')) ? 'satisfied' : 'partial',
    'real-time-processing': components.some(c => c.tags?.includes('real-time') || c.tags?.includes('streaming')) ? 'satisfied' : 'partial',
    'regulatory-compliance': components.some(c => c.layerId === 'layer12') ? 'satisfied' : 'partial',
    'monitoring-observability': components.some(c => c.layerId === 'layer11') ? 'satisfied' : 'partial'
  };
  return statusMap[reqId] || 'partial';
};

// ==================== MAIN COMPONENT ====================

const ApproachTraceabilityView = () => {
  const { state, dispatch } = useSolution();
  const [viewMode, setViewMode] = useState(VIEW_MODES.HIERARCHICAL);
  const [groupingMode, setGroupingMode] = useState(GROUPING_MODES.NONE);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [traceDirection, setTraceDirection] = useState('bidirectional');
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const mainContainerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1400, height: 800 });

  // ==================== DERIVE REQUIREMENTS ====================

  const requirements = useMemo(() => {
    const components = state.selectedComponents;
    const constraints = state.approachConstraints || {};
    const reqCategories = getAllRequirementCategories();
    
    return reqCategories.map(req => {
      const approach = getExactApproach(req.id, constraints);
      return {
        ...req,
        status: getRequirementStatus(req.id, components),
        approachId: approach?.approachId || 8,
        approachName: approach?.approachName || 'Hybrid Approach'
      };
    });
  }, [state.selectedComponents, state.approachConstraints]);

  // ==================== BUILD ALLUVIAL DATA WITH GROUPING ====================

  const alluvialData = useMemo(() => {
    const nodes = [];
    const links = [];
    const nodeMap = new Map();
    
    // COLUMN 1: REQUIREMENTS
    requirements.forEach(req => {
      const reqNode = {
        id: `req-${req.id}`,
        name: req.name,
        type: 'requirement',
        category: req.category,
        priority: req.priority,
        status: req.status,
        icon: REQUIREMENT_ICONS[req.category] || '📋',
        color: REQUIREMENT_COLORS[req.category] || '#94a3b8',
        approachId: req.approachId
      };
      nodes.push(reqNode);
      nodeMap.set(reqNode.id, reqNode);
    });
    
    // COLUMN 2: APPROACHES (deduplicated)
    const approachSet = new Map();
    requirements.forEach(req => {
      const approachKey = `app-${req.approachId}`;
      if (!approachSet.has(approachKey)) {
        approachSet.set(approachKey, {
          id: approachKey,
          name: req.approachName,
          type: 'approach',
          icon: APPROACH_ICONS[req.approachId] || '🎯',
          approachId: req.approachId
        });
        nodes.push(approachSet.get(approachKey));
        nodeMap.set(approachKey, approachSet.get(approachKey));
      }
      links.push({ source: `req-${req.id}`, target: approachKey, type: 'requirement-to-approach' });
    });
    
    // COLUMN 3: LAYERS (if hierarchical)
    if (viewMode === VIEW_MODES.HIERARCHICAL) {
      const solutionLayers = new Map();
      state.selectedComponents.forEach(comp => {
        const layerKey = comp.layerId || 'unknown';
        if (!solutionLayers.has(layerKey)) {
          solutionLayers.set(layerKey, {
            id: `sol-layer-${layerKey}`,
            name: comp.layerName || getLayerName(layerKey),
            type: 'solution-layer',
            layerId: layerKey
          });
          nodes.push(solutionLayers.get(layerKey));
          nodeMap.set(`sol-layer-${layerKey}`, solutionLayers.get(layerKey));
        }
      });
      
      approachSet.forEach((approachNode) => {
        const mappedLayers = getApproachLayers(approachNode.approachId);
        mappedLayers.forEach(layerId => {
          if (solutionLayers.has(layerId)) {
            links.push({ source: approachNode.id, target: `sol-layer-${layerId}`, type: 'approach-to-layer' });
          }
        });
      });
      
      // COLUMN 4: SUB-LAYERS
      const subLayers = new Map();
      state.selectedComponents.forEach(comp => {
        const subLayerKey = comp.subLayerId || 'unknown';
        if (!subLayers.has(subLayerKey)) {
          subLayers.set(subLayerKey, {
            id: `sol-sublayer-${subLayerKey}`,
            name: comp.subLayerName || 'Sub-Layer',
            type: 'sub-layer',
            subLayerId: subLayerKey,
            layerId: comp.layerId
          });
          nodes.push(subLayers.get(subLayerKey));
          nodeMap.set(`sol-sublayer-${subLayerKey}`, subLayers.get(subLayerKey));
          links.push({ source: `sol-layer-${comp.layerId}`, target: `sol-sublayer-${subLayerKey}`, type: 'layer-to-sublayer' });
        }
      });
      
      // COLUMN 5: COMPONENTS (Grouped or Individual)
      if (groupingMode === GROUPING_MODES.NONE) {
        // Individual components
        state.selectedComponents.forEach(comp => {
          const compNode = {
            id: `comp-${comp.id}`,
            name: comp.name,
            type: 'component',
            icon: comp.icon || '🔧'
          };
          nodes.push(compNode);
          nodeMap.set(compNode.id, compNode);
          links.push({ source: `sol-sublayer-${comp.subLayerId}`, target: compNode.id, type: 'sublayer-to-component' });
        });
      } else {
        // Grouped components
        const groups = getGroupedComponents(state.selectedComponents, groupingMode);
        
        groups.forEach(group => {
          const groupId = `group-${group.groupId}`;
          const isExpanded = expandedGroups.has(groupId);
          
          // Group node
          const groupNode = {
            id: groupId,
            name: group.groupName,
            type: 'group',
            icon: group.groupIcon,
            color: group.groupColor,
            componentCount: group.components.length,
            isExpanded,
            groupingMode
          };
          nodes.push(groupNode);
          nodeMap.set(groupId, groupNode);
          
          // Link sub-layers to group
          group.components.forEach(comp => {
            const subLayerId = `sol-sublayer-${comp.subLayerId}`;
            if (nodeMap.has(subLayerId)) {
              links.push({ source: subLayerId, target: groupId, type: 'sublayer-to-group' });
            }
          });
          
          // If expanded, show individual components
          if (isExpanded) {
            group.components.forEach(comp => {
              const compNode = {
                id: `comp-${comp.id}`,
                name: comp.name,
                type: 'component',
                icon: comp.icon || '🔧'
              };
              nodes.push(compNode);
              nodeMap.set(compNode.id, compNode);
              links.push({ source: groupId, target: compNode.id, type: 'group-to-component' });
            });
          }
        });
      }
    } else {
      // DIRECT MODE
      if (groupingMode === GROUPING_MODES.NONE) {
        state.selectedComponents.forEach(comp => {
          const compNode = {
            id: `comp-${comp.id}`,
            name: comp.name,
            type: 'component',
            icon: comp.icon || '🔧'
          };
          nodes.push(compNode);
          nodeMap.set(compNode.id, compNode);
        });
        
        approachSet.forEach((approachNode) => {
          const mappedLayers = getApproachLayers(approachNode.approachId);
          state.selectedComponents.forEach(comp => {
            if (mappedLayers.includes(comp.layerId)) {
              links.push({ source: approachNode.id, target: `comp-${comp.id}`, type: 'approach-to-component' });
            }
          });
        });
      } else {
        // Grouped direct mode
        const groups = getGroupedComponents(state.selectedComponents, groupingMode);
        
        groups.forEach(group => {
          const groupId = `group-${group.groupId}`;
          const isExpanded = expandedGroups.has(groupId);
          
          const groupNode = {
            id: groupId,
            name: group.groupName,
            type: 'group',
            icon: group.groupIcon,
            color: group.groupColor,
            componentCount: group.components.length,
            isExpanded,
            groupingMode
          };
          nodes.push(groupNode);
          nodeMap.set(groupId, groupNode);
          
          approachSet.forEach((approachNode) => {
            const mappedLayers = getApproachLayers(approachNode.approachId);
            group.components.forEach(comp => {
              if (mappedLayers.includes(comp.layerId)) {
                links.push({ source: approachNode.id, target: groupId, type: 'approach-to-group' });
              }
            });
          });
          
          if (isExpanded) {
            group.components.forEach(comp => {
              const compNode = {
                id: `comp-${comp.id}`,
                name: comp.name,
                type: 'component',
                icon: comp.icon || '🔧'
              };
              nodes.push(compNode);
              nodeMap.set(compNode.id, compNode);
              links.push({ source: groupId, target: compNode.id, type: 'group-to-component' });
            });
          }
        });
      }
    }
    
    return { nodes, links, nodeMap };
  }, [requirements, state.selectedComponents, viewMode, groupingMode, expandedGroups]);

  // ==================== POSITION NODES ====================

  const positionedNodes = useMemo(() => {
    const { nodes } = alluvialData;
    
    const reqNodes = nodes.filter(n => n.type === 'requirement');
    const approachNodes = nodes.filter(n => n.type === 'approach');
    const solLayerNodes = nodes.filter(n => n.type === 'solution-layer');
    const subLayerNodes = nodes.filter(n => n.type === 'sub-layer');
    const groupNodes = nodes.filter(n => n.type === 'group');
    const compNodes = nodes.filter(n => n.type === 'component');
    
    let columnCount = 5;
    if (viewMode === VIEW_MODES.DIRECT) columnCount = 3;
    if (groupingMode !== GROUPING_MODES.NONE) columnCount = viewMode === VIEW_MODES.HIERARCHICAL ? 6 : 4;
    
    const columnWidth = dimensions.width / columnCount;
    const nodeHeight = 28;
    const nodeGap = 5;
    const headerHeight = 50;
    
    const positionColumn = (nodeList, columnIndex) => {
      const totalHeight = nodeList.length * (nodeHeight + nodeGap);
      const availableHeight = dimensions.height - headerHeight - 30;
      const startY = Math.max(headerHeight + 10, (availableHeight - totalHeight) / 2 + headerHeight);
      
      nodeList.forEach((node, index) => {
        node.x = columnIndex * columnWidth + columnWidth / 2;
        node.y = startY + index * (nodeHeight + nodeGap);
        node.width = Math.max(columnWidth - 30, 80);
        node.height = nodeHeight;
        node.column = columnIndex;
      });
    };
    
    let colIndex = 0;
    positionColumn(reqNodes, colIndex++);       // 0
    positionColumn(approachNodes, colIndex++);   // 1
    
    if (viewMode === VIEW_MODES.HIERARCHICAL) {
      positionColumn(solLayerNodes, colIndex++); // 2
      positionColumn(subLayerNodes, colIndex++); // 3
      
      if (groupingMode !== GROUPING_MODES.NONE) {
        positionColumn(groupNodes, colIndex++);  // 4
        positionColumn(compNodes, colIndex);     // 5 (if expanded)
      } else {
        positionColumn(compNodes, colIndex);     // 4
      }
    } else {
      if (groupingMode !== GROUPING_MODES.NONE) {
        positionColumn(groupNodes, colIndex++);  // 2
        positionColumn(compNodes, colIndex);     // 3 (if expanded)
      } else {
        positionColumn(compNodes, colIndex);     // 2
      }
    }
    
    return nodes;
  }, [alluvialData, dimensions, viewMode, groupingMode]);

  // ==================== TOGGLE GROUP ====================

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) newSet.delete(groupId);
      else newSet.add(groupId);
      return newSet;
    });
  }, []);

  const expandAllGroups = useCallback(() => {
    const allGroupIds = alluvialData.nodes.filter(n => n.type === 'group').map(n => n.id);
    setExpandedGroups(new Set(allGroupIds));
  }, [alluvialData]);

  const collapseAllGroups = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  // ==================== HIGHLIGHT LOGIC ====================

  const getHighlightedIds = useCallback(() => {
    const highlighted = new Set();
    if (!selectedNode) return highlighted;
    
    highlighted.add(selectedNode.id);
    
    const traceForward = (startId) => {
      const visited = new Set();
      const queue = [startId];
      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        highlighted.add(currentId);
        alluvialData.links.forEach(link => {
          if (link.source === currentId && !visited.has(link.target)) queue.push(link.target);
        });
      }
    };
    
    const traceBackward = (startId) => {
      const visited = new Set();
      const queue = [startId];
      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        highlighted.add(currentId);
        alluvialData.links.forEach(link => {
          if (link.target === currentId && !visited.has(link.source)) queue.push(link.source);
        });
      }
    };
    
    if (traceDirection === 'forward' || traceDirection === 'bidirectional') traceForward(selectedNode.id);
    if (traceDirection === 'backward' || traceDirection === 'bidirectional') traceBackward(selectedNode.id);
    
    return highlighted;
  }, [selectedNode, alluvialData, traceDirection]);

  // ==================== FULLSCREEN & ZOOM ====================

  const toggleFullscreen = useCallback(() => {
    const element = mainContainerRef.current;
    if (!element) return;
    if (!document.fullscreenElement) {
      element.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F11') { e.preventDefault(); toggleFullscreen(); }
      if (e.key === 'Escape' && !document.fullscreenElement) setSelectedNode(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen]);

  const setupZoom = useCallback(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const zoom = d3.zoom().scaleExtent([0.1, 5]).on('zoom', (event) => setTransform(event.transform));
    svg.call(zoom);
    svg.on('dblclick.zoom', null);
  }, []);

  useEffect(() => { setTimeout(() => setupZoom(), 100); }, [setupZoom]);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: Math.max(rect.width, 1000), height: Math.max(rect.height, 600) });
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    const observer = new ResizeObserver(updateDimensions);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => { window.removeEventListener('resize', updateDimensions); observer.disconnect(); };
  }, []);

  const stats = useMemo(() => ({
    requirements: requirements.length,
    approaches: new Set(requirements.map(r => r.approachId)).size,
    solutionLayers: new Set(state.selectedComponents.map(c => c.layerId)).size,
    subLayers: new Set(state.selectedComponents.map(c => c.subLayerId)).size,
    components: state.selectedComponents.length,
    groups: alluvialData.nodes.filter(n => n.type === 'group').length
  }), [requirements, state.selectedComponents, alluvialData]);

  // ==================== RENDER ====================

  return (
    <div 
      ref={mainContainerRef}
      className={`h-full flex flex-col ${isFullscreen ? 'bg-white' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}
      style={isFullscreen ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiTarget className="w-6 h-6 text-blue-600" />
              Approach Traceability with Grouping
            </h2>
            <p className="text-sm text-gray-500">
              Requirements → Approaches → {viewMode === VIEW_MODES.HIERARCHICAL ? 'Layers → Sub-Layers →' : ''} Components
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* View Mode */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setViewMode(VIEW_MODES.HIERARCHICAL)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${viewMode === VIEW_MODES.HIERARCHICAL ? 'bg-white shadow text-blue-600' : 'text-gray-600'}`}>📊 Hierarchical</button>
              <button onClick={() => setViewMode(VIEW_MODES.DIRECT)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${viewMode === VIEW_MODES.DIRECT ? 'bg-white shadow text-blue-600' : 'text-gray-600'}`}>⚡ Direct</button>
            </div>
            
            {/* Grouping Mode */}
            <div className="flex gap-1 bg-orange-50 rounded-lg p-1">
              <button onClick={() => setGroupingMode(GROUPING_MODES.NONE)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${groupingMode === GROUPING_MODES.NONE ? 'bg-orange-500 text-white' : 'text-orange-600'}`}>Individual</button>
              <button onClick={() => setGroupingMode(GROUPING_MODES.TYPE)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${groupingMode === GROUPING_MODES.TYPE ? 'bg-orange-500 text-white' : 'text-orange-600'}`}>📦 By Type</button>
              <button onClick={() => setGroupingMode(GROUPING_MODES.SEQUENCE)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${groupingMode === GROUPING_MODES.SEQUENCE ? 'bg-orange-500 text-white' : 'text-orange-600'}`}>📅 By Sequence</button>
            </div>
            
            {/* Group Expand/Collapse */}
            {groupingMode !== GROUPING_MODES.NONE && (
              <div className="flex gap-1 bg-green-50 rounded-lg p-1">
                <button onClick={expandAllGroups} className="px-3 py-1.5 rounded-md text-xs font-semibold text-green-600">Expand All</button>
                <button onClick={collapseAllGroups} className="px-3 py-1.5 rounded-md text-xs font-semibold text-green-600">Collapse All</button>
              </div>
            )}
            
            {/* Trace Direction */}
            <div className="flex gap-1 bg-purple-50 rounded-lg p-1">
              <button onClick={() => setTraceDirection('forward')} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${traceDirection === 'forward' ? 'bg-purple-500 text-white' : 'text-purple-600'}`}>→</button>
              <button onClick={() => setTraceDirection('bidirectional')} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${traceDirection === 'bidirectional' ? 'bg-purple-500 text-white' : 'text-purple-600'}`}>↔</button>
              <button onClick={() => setTraceDirection('backward')} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${traceDirection === 'backward' ? 'bg-purple-500 text-white' : 'text-purple-600'}`}>←</button>
            </div>
            
            {/* Zoom */}
            <button onClick={() => setTransform(prev => ({ ...prev, k: Math.min(prev.k * 1.2, 3) }))} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"><FiZoomIn className="w-4 h-4" /></button>
            <button onClick={() => setTransform(prev => ({ ...prev, k: Math.max(prev.k / 1.2, 0.1) }))} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"><FiZoomOut className="w-4 h-4" /></button>
            <button onClick={() => setTransform({ x: 0, y: 0, k: 1 })} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"><FiCrosshair className="w-4 h-4" /></button>
            
            <button onClick={() => setShowLabels(!showLabels)} className={`p-2 rounded-lg ${showLabels ? 'bg-blue-100 text-blue-600' : 'bg-gray-100'}`}><FiEye className="w-4 h-4" /></button>
            <button onClick={toggleFullscreen} className={`p-2 rounded-lg ${isFullscreen ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}>{isFullscreen ? <FiMinimize className="w-4 h-4" /> : <FiMaximize2 className="w-4 h-4" />}</button>
            
            {selectedNode && <button onClick={() => setSelectedNode(null)} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-semibold"><FiX className="w-3 h-3" /> Clear</button>}
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <div className="p-2 bg-blue-50 rounded-lg text-center"><div className="text-lg font-bold text-blue-600">{stats.requirements}</div><div className="text-xs text-gray-600">Requirements</div></div>
          <div className="p-2 bg-green-50 rounded-lg text-center"><div className="text-lg font-bold text-green-600">{stats.approaches}</div><div className="text-xs text-gray-600">Approaches</div></div>
          <div className="p-2 bg-orange-50 rounded-lg text-center"><div className="text-lg font-bold text-orange-600">{stats.solutionLayers}</div><div className="text-xs text-gray-600">Layers</div></div>
          <div className="p-2 bg-pink-50 rounded-lg text-center"><div className="text-lg font-bold text-pink-600">{stats.subLayers}</div><div className="text-xs text-gray-600">Sub-Layers</div></div>
          <div className="p-2 bg-teal-50 rounded-lg text-center"><div className="text-lg font-bold text-teal-600">{stats.components}</div><div className="text-xs text-gray-600">Components</div></div>
          <div className="p-2 bg-purple-50 rounded-lg text-center"><div className="text-lg font-bold text-purple-600">{stats.groups}</div><div className="text-xs text-gray-600">Groups</div></div>
        </div>
      </div>
      
      {/* Alluvial SVG */}
      <div className="flex-1 overflow-hidden relative" ref={containerRef}>
        <svg ref={svgRef} width={dimensions.width} height={dimensions.height} className="w-full h-full" style={{ cursor: 'grab', touchAction: 'none' }}>
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.15" />
            </filter>
          </defs>
          
          <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
            {/* Links */}
            {alluvialData.links.map((link, i) => {
              const source = alluvialData.nodeMap.get(link.source);
              const target = alluvialData.nodeMap.get(link.target);
              if (!source || !target) return null;
              
              const highlightedIds = getHighlightedIds();
              const isHighlighted = highlightedIds.has(source.id) && highlightedIds.has(target.id);
              const isDimmed = highlightedIds.size > 0 && !isHighlighted;
              
              const sourceX = source.x + source.width / 2;
              const targetX = target.x - target.width / 2;
              const sourceY = source.y + source.height / 2;
              const targetY = target.y + target.height / 2;
              
              const linkColor = link.type === 'requirement-to-approach' ? '#3b82f6' :
                                link.type === 'approach-to-layer' ? '#10b981' :
                                link.type === 'approach-to-component' ? '#ef4444' :
                                link.type === 'approach-to-group' ? '#ef4444' :
                                link.type === 'layer-to-sublayer' ? '#ec4899' :
                                link.type === 'sublayer-to-component' ? '#f97316' :
                                link.type === 'sublayer-to-group' ? '#f97316' :
                                link.type === 'group-to-component' ? '#8b5cf6' : '#94a3b8';
              
              return (
                <path key={i} d={`M${sourceX},${sourceY} C${(sourceX + targetX) / 2},${sourceY} ${(sourceX + targetX) / 2},${targetY} ${targetX},${targetY}`}
                  fill="none" stroke={isHighlighted ? '#ef4444' : linkColor} strokeWidth={isHighlighted ? 2.5 : 1.2}
                  strokeOpacity={isDimmed ? 0.04 : isHighlighted ? 0.9 : 0.25}
                  strokeDasharray={link.type.includes('group') ? '5,3' : 'none'}
                  style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }} />
              );
            })}
            
            {/* Nodes */}
            {alluvialData.nodes.map((node, i) => {
              const highlightedIds = getHighlightedIds();
              const isHighlighted = highlightedIds.has(node.id);
              const isDimmed = highlightedIds.size > 0 && !isHighlighted;
              const isHovered = hoveredNode === node.id;
              const isSelected = selectedNode?.id === node.id;
              
              const nodeX = node.x - node.width / 2;
              const nodeY = node.y;
              
              return (
                <g key={i} transform={`translate(${nodeX}, ${nodeY})`}
                  style={{ cursor: 'pointer', opacity: isDimmed ? 0.15 : 1, transition: 'opacity 0.3s ease' }}
                  onClick={() => {
                    if (node.type === 'group') {
                      toggleGroup(node.id);
                      setSelectedNode(selectedNode?.id === node.id ? null : node);
                    } else {
                      setSelectedNode(selectedNode?.id === node.id ? null : node);
                    }
                  }}
                  onMouseEnter={(e) => { setHoveredNode(node.id); setTooltip({ visible: true, x: e.clientX, y: e.clientY, content: node }); }}
                  onMouseLeave={() => { setHoveredNode(null); setTooltip({ visible: false, x: 0, y: 0, content: null }); }}
                >
                  <rect width={node.width} height={node.height} rx={6} ry={6}
                    fill={node.type === 'requirement' ? '#dbeafe' :
                          node.type === 'approach' ? '#f0fdf4' :
                          node.type === 'solution-layer' ? '#ffedd5' :
                          node.type === 'sub-layer' ? '#fce7f3' :
                          node.type === 'group' ? node.color + '30' : '#f1f5f9'}
                    stroke={isSelected ? '#ef4444' : isHighlighted ? '#3b82f6' : isHovered ? '#60a5fa' : node.type === 'group' ? node.color : '#cbd5e1'}
                    strokeWidth={isSelected || isHighlighted ? 2.5 : isHovered ? 2 : node.type === 'group' ? 2 : 1}
                    filter={isHighlighted ? 'url(#glow)' : 'url(#shadow)'}
                    style={{ transition: 'all 0.3s ease' }} />
                  
                  <text x={5} y={node.height / 2 + 4} fontSize="9" fontWeight={node.type === 'requirement' || node.type === 'approach' || node.type === 'group' ? 'bold' : 'normal'} fill="#1f2937">
                    {showLabels ? `${node.icon || ''} ${node.name.length > 25 ? node.name.substring(0, 23) + '...' : node.name}` : (node.icon || '')}
                  </text>
                  
                  {node.type === 'requirement' && <circle cx={node.width - 8} cy={node.height / 2} r={4} fill={node.status === 'satisfied' ? '#10b981' : '#f59e0b'} stroke="white" strokeWidth={1} />}
                  
                  {node.type === 'group' && (
                    <>
                      <text x={node.width - 25} y={node.height / 2 + 4} fontSize="8" fill="#64748b">{node.componentCount}</text>
                      <text x={node.width - 12} y={node.height / 2 + 4} fontSize="10" fontWeight="bold" fill="#64748b">
                        {node.isExpanded ? '−' : '+'}
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
        
        {/* Tooltip */}
        <AnimatePresence>
          {tooltip.visible && tooltip.content && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              className="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-200 p-4 max-w-xs pointer-events-none"
              style={{ left: tooltip.x + 15, top: tooltip.y + 15 }}>
              <div className="font-bold text-gray-900 mb-1">{tooltip.content.icon} {tooltip.content.name}</div>
              <div className="text-xs text-gray-500 mb-2">Type: {tooltip.content.type}</div>
              {tooltip.content.componentCount !== undefined && (
                <div className="text-xs text-gray-600">Components: {tooltip.content.componentCount}</div>
              )}
              {tooltip.content.type === 'group' && (
                <div className="text-xs text-blue-600 mt-2">Click to {tooltip.content.isExpanded ? 'collapse' : 'expand'} group</div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Zoom Hint */}
        <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur rounded-lg px-3 py-2 text-xs text-gray-500 shadow-sm border border-gray-200 pointer-events-none">
          🖱️ Scroll zoom • Drag pan • Click groups to expand/collapse
        </div>
      </div>
      
      {/* Legend */}
      <div className="bg-white border-t border-gray-200 p-2">
        <div className="flex items-center gap-4 text-xs text-gray-600 flex-wrap">
          <span className="font-semibold">Grouping:</span>
          <span>{groupingMode === 'none' ? 'Individual Components' : groupingMode === 'type' ? 'By Component Type' : 'By Implementation Sequence'}</span>
          <span className="text-gray-400">|</span>
          <span>Click group nodes (colored) to expand/collapse</span>
        </div>
      </div>
    </div>
  );
};

export default ApproachTraceabilityView;